import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CssBaseline from '@mui/material/CssBaseline';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const APL_LOGO_LIGHT = '/images/apl_small_vertical_blue.png';
const APL_LOGO_DARK = '/images/apl_small_vertical_white.png';
const AMX_LOGO = '/images/AMX-Logo.png';

const developers = [
  'Ahmed Al Bayati',
  'Collin Katz',
  'Daniel Campbell',
  'Logan Kirkpatric',
  'Noah Reneau',
  'Victor Zhu',
  'Piper Floyd',
];

const aboutLogoPreloads = [AMX_LOGO, APL_LOGO_LIGHT, APL_LOGO_DARK];

function preloadAboutLogos() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.__amxAboutLogoPreloads = window.__amxAboutLogoPreloads || [];

  aboutLogoPreloads.forEach((src) => {
    if (!document.querySelector(`link[data-amx-about-logo-preload="${src}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.type = 'image/png';
      link.fetchPriority = 'high';
      link.setAttribute('data-amx-about-logo-preload', src);
      document.head.appendChild(link);
    }

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    image.src = src;

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }

    window.__amxAboutLogoPreloads.push(image);
  });
}

preloadAboutLogos();

function AplLogo() {
  const { mode, systemMode } = useColorScheme();
  const resolvedMode = mode === 'system' ? systemMode : mode;
  const showDarkLogo = resolvedMode === 'dark';

  return (
    <Box
      role="img"
      aria-label="Johns Hopkins University Applied Physics Laboratory logo"
      sx={{
        position: 'relative',
        width: { xs: 'min(68vw, 380px)', md: 440 },
        aspectRatio: '1330 / 870',
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={APL_LOGO_LIGHT}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        fetchPriority={showDarkLogo ? 'low' : 'high'}
        width="1330"
        height="870"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          opacity: showDarkLogo ? 0 : 1,
        }}
      />
      <Box
        component="img"
        src={APL_LOGO_DARK}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        fetchPriority={showDarkLogo ? 'high' : 'low'}
        width="1330"
        height="870"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          opacity: showDarkLogo ? 1 : 0,
        }}
      />
    </Box>
  );
}

function AboutContent() {
  return (
    <Box sx={{ display: 'flex' }}>
      <SideMenu />
      <AppNavbar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: '100vh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, md: 3 },
          pt: { xs: 11, md: 3 },
        }}
      >
        <Header
          breadcrumbItems={[
            { label: 'AMX xConsole', href: '/' },
            { label: 'About' },
          ]}
        />

        <Stack
          spacing={4}
          sx={{
            width: '100%',
            maxWidth: 1000,
            mx: 'auto',
            flexGrow: 1,
            justifyContent: 'flex-start',
            alignItems: 'center',
            py: { xs: 3, md: 4 },
          }}
        >
          <Stack
            direction="row"
            spacing={{ xs: 3, md: 8 }}
            justifyContent="center"
            alignItems="center"
            sx={{
              flexWrap: 'wrap',
            }}
          >
            <Box
              component="img"
              src={AMX_LOGO}
              alt="AMX logo"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width="853"
              height="430"
              sx={{
                width: { xs: 'min(56vw, 270px)', md: 340 },
                maxHeight: { xs: 200, md: 300 },
                objectFit: 'contain',
              }}
            />
            <AplLogo />
          </Stack>

          <Box>
            <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 700 }}>
              About AMX xConsole
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              AMX xConsole was developed by Johns Hopkins
              University Applied Physics Laboratory to enable agile manufacturing
              capabilities and streamline workflows. It provides a unified platform
              for managing assets, capabilities, and requests.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom align="center" sx={{ fontWeight: 700 }}>
              Development Team
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
              {developers.map((name) => (
                <Chip key={name} label={name} variant="outlined" />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" align="center" color="text.secondary">
              For questions or support, contact us at{' '}
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{ textDecoration: 'underline' }}
              >
                <a
                  href="mailto:svc-amx@jhuapl.edu"
                  style={{ color: 'inherit', textDecoration: 'inherit' }}
                >
                  svc-amx@jhuapl.edu
                </a>
              </Typography>
              .
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

export default function About(props) {
  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <AboutContent />
    </AppTheme>
  );
}
